import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { CallbackForm } from './CallbackForm';
import { useTranslation } from '../i18n';
interface CallbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function CallbackModal({
  isOpen,
  onClose
}: CallbackModalProps) {
  const {
    lang
  } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset modal scroll to top
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);
  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) {
      onClose();
    }
  };
  if (!isOpen) return null;
  return <div ref={backdropRef} onClick={handleBackdropClick} className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" data-id="element-179" data-mp="28cgc4p5z6nkx">
      <div ref={contentRef} className="relative w-full sm:max-w-md max-h-[90vh] overflow-y-auto bg-ivory rounded-t-2xl sm:rounded-2xl shadow-2xl animate-slide-up" data-id="element-180" data-mp="311x2lha1buei">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-stone-200 hover:bg-stone-300 text-charcoal-light hover:text-charcoal transition-colors" aria-label="Close" data-id="element-181" data-mp="2r2665a1wz5bs">
          <X className="w-4 h-4" data-id="element-182" data-mp="3ib9292ya2sy7" />
        </button>

        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden" data-id="element-183" data-mp="3dbwgklj57rx7">
          <div className="w-10 h-1 rounded-full bg-beige" data-id="element-184" data-mp="3dceinyshl0d1" />
        </div>

        {/* Form */}
        <div className="p-1" data-id="element-185" data-mp="38j3de23born8">
          <CallbackForm data-id="element-186" data-mp="33ltd1reeavkm" />
        </div>
      </div>

      <style data-id="element-187" data-mp="33l1qf62s086h">{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>;
}